import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { Input } from "./input";
import ApiKeySuccessDialog from "./ApiKeySuccessDialog";
import { Spinner } from "./ui/spinner";
import { useCreateApiKey } from "../hooks/useApiKeys";
import { projectsApi } from "../services/rag-service";
import type { ApiKeyWithValue } from "../services/api-keys-rag-service";
import { cn } from "../utils/cn";

type ProjectOption = {
  id: string;
  name?: string;
  displayName?: string;
};

const API_KEY_SCOPES = [
  "llm",
  "parsing",
  "extraction",
  "summarize",
  "agentic",
] as const;

const preprocessBudgetValue = (value: any) => {
  if (value === "" || value == null) {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const str = String(value).trim();
  const parsed = Number(str);
  return Number.isNaN(parsed) ? value : parsed;
};

const budgetNumberSchemaRequired = z
  .number()
  .min(0, "Budget must be a positive number")
  .refine((n) => /^\d+(?:\.\d{1,2})?$/.test(String(n)), {
    message: "Only up to two decimal places are allowed",
  });

const createApiKeySchema = (t: (key: string) => string) => {
  const baseFields = {
    name: z
      .string()
      .trim()
      .min(1, t("apiKeys.keyNameRequired"))
      .max(64, "Name must be at most 64 characters"),
    projectId: z.string().min(1, t("apiKeys.projectRequired")),
    expiresIn: z.string(),
    scopes: z.array(z.enum(API_KEY_SCOPES)).min(1, t("apiKeys.scopeRequired")),
  };

  const schemaNoFreq = z.object({
    ...baseFields,
    budget_reset_frequency: z.literal("").optional(),
    // When no frequency is selected, max_budget_usd must be null/absent
    max_budget_usd: z.null().optional(),
  });

  const schemaWithFreq = z.object({
    ...baseFields,
    budget_reset_frequency: z.enum(["HOURLY", "DAILY", "WEEKLY", "MONTHLY"]),
    max_budget_usd: z.preprocess(
      preprocessBudgetValue,
      budgetNumberSchemaRequired,
    ),
  });

  return z.union([schemaNoFreq, schemaWithFreq]);
};

type CreateApiKeyFormData = z.infer<ReturnType<typeof createApiKeySchema>>;

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onKeyCreated?: (key: ApiKeyWithValue) => void;
}

const CreateApiKeyDialog: React.FC<CreateApiKeyDialogProps> = ({
  open,
  onOpenChange,
  onKeyCreated,
}) => {
  const { t } = useTranslation();
  const [createdKey, setCreatedKey] = useState<ApiKeyWithValue | null>(null);
  const [scopesOpen, setScopesOpen] = useState(false);
  const validationSchema = useMemo(() => createApiKeySchema(t), [t]);

  const {
    register,
    control,
    clearErrors,
    getValues,
    reset,
    setError,
    formState: { errors },
  } = useForm<CreateApiKeyFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: "",
      projectId: "",
      expiresIn: "never",
      scopes: ["llm"],
      max_budget_usd: null,
      budget_reset_frequency: "",
    },
  });

  const mutation = useCreateApiKey();

  const organizationId =
    typeof window !== "undefined"
      ? localStorage.getItem("currentOrganizationId")
      : null;

  const projectsQuery = useQuery<ProjectOption[]>({
    queryKey: ["apiKeyProjects", organizationId],
    queryFn: async () => {
      const projects = await projectsApi.getProjects({ includeShared: true });
      if (!Array.isArray(projects)) {
        return [];
      }

      return [...projects].sort((left, right) => {
        const leftName = left.name ?? left.displayName ?? "";
        const rightName = right.name ?? right.displayName ?? "";
        return leftName.localeCompare(rightName);
      });
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const projects = projectsQuery.data ?? [];
  const isProjectsLoading = projectsQuery.isLoading;
  const isCreatingKey = mutation.isPending;

  const resetDialogState = useCallback(() => {
    setCreatedKey(null);
    reset({
      name: "",
      projectId: "",
      expiresIn: "never",
      scopes: ["llm"],
      max_budget_usd: null,
      budget_reset_frequency: "",
    });
    mutation.reset();
  }, [mutation, reset]);

  const handleFormClose = useCallback(() => {
    resetDialogState();
    onOpenChange(false);
  }, [onOpenChange, resetDialogState]);

  const handleCreatedKeyClose = useCallback(() => {
    resetDialogState();
    onOpenChange(false);
  }, [onOpenChange, resetDialogState]);

  const setServerErrors = useCallback(
    (error: any) => {
      const detail = error?.response?.data?.detail;
      if (!Array.isArray(detail)) {
        return;
      }

      detail.forEach((item: any) => {
        if (!item?.loc || !item?.msg) {
          return;
        }

        const fieldName = item.loc[item.loc.length - 1];
        const errorMessage = item.msg;

        switch (fieldName) {
          case "budget_reset_frequency":
            setError("budget_reset_frequency", {
              type: "server",
              message: errorMessage,
            });
            break;
          case "max_budget_usd":
            setError("max_budget_usd", {
              type: "server",
              message: errorMessage,
            });
            break;
          case "project_id":
            setError("projectId", {
              type: "server",
              message: errorMessage,
            });
            break;
          case "name":
            setError("name", {
              type: "server",
              message: errorMessage,
            });
            break;
          default:
            break;
        }
      });
    },
    [setError],
  );

  const createKey = useCallback(
    (data: CreateApiKeyFormData) => {
      let expires_at = null;

      if (data.expiresIn !== "never") {
        const days = Number.parseInt(data.expiresIn, 10);
        if (!Number.isNaN(days)) {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + days);
          expires_at = expirationDate.toISOString();
        }
      }

      const payload: any = {
        name: data.name.trim(),
        project_id: data.projectId,
        scopes: data.scopes,
        expires_at,
      };

      if (data.max_budget_usd != null) {
        payload.max_budget_usd = data.max_budget_usd;
      }

      // Only include budget_reset_frequency when a value was selected
      if (data.budget_reset_frequency) {
        payload.budget_reset_frequency = data.budget_reset_frequency;
      }

      mutation.mutate(payload, {
        onSuccess: (responseData) => {
          setCreatedKey(responseData);
          onKeyCreated?.(responseData);
        },
        onError: (error: any) => {
          setServerErrors(error);
        },
      });
    },
    [mutation, onKeyCreated, setServerErrors],
  );

  const handleCreateKey = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const formValues = getValues();

      // Normalize max budget input so empty string is treated as null and
      // numeric strings are parsed to numbers. This ensures consistent
      // validation behavior (also preserves 0 correctly).
      const normalizedMaxBudget = preprocessBudgetValue(
        formValues.max_budget_usd,
      );

      // Quick client-side checks:
      // - if a budget reset frequency was chosen, require max_budget_usd
      if (
        formValues.budget_reset_frequency &&
        (normalizedMaxBudget === null || normalizedMaxBudget === undefined)
      ) {
        setError("max_budget_usd", {
          type: "manual",
          message: t("apiKeys.maxBudgetNote"),
        });
        return;
      }

      // - if a max_budget_usd was entered, require budget_reset_frequency
      if (
        normalizedMaxBudget !== null &&
        normalizedMaxBudget !== undefined &&
        String(normalizedMaxBudget).trim() !== "" &&
        (!formValues.budget_reset_frequency ||
          String(formValues.budget_reset_frequency).trim() === "")
      ) {
        setError("budget_reset_frequency", {
          type: "manual",
          message: t("apiKeys.budgetResetFrequencyNote"),
        });
        return;
      }

      const parsedForm = validationSchema.safeParse({
        ...formValues,
        name: formValues.name.trim(),
        projectId: formValues.projectId,
        // Use normalized value; pass undefined only when absent so 0 is preserved
        max_budget_usd:
          normalizedMaxBudget === null || normalizedMaxBudget === undefined
            ? undefined
            : normalizedMaxBudget,
        budget_reset_frequency:
          formValues.budget_reset_frequency &&
          String(formValues.budget_reset_frequency).trim() !== ""
            ? formValues.budget_reset_frequency
            : undefined,
        expiresIn: formValues.expiresIn,
      });

      console.log("Parsed form:", parsedForm);
      clearErrors();

      if (!parsedForm.success) {
        const fieldErrors = parsedForm.error.flatten().fieldErrors;
        const nameError = fieldErrors.name?.[0];
        const projectError = fieldErrors.projectId?.[0];
        const scopesError = fieldErrors.scopes?.[0];
        const maxBudgetError = fieldErrors.max_budget_usd?.[0];
        const budgetResetFrequencyError =
          fieldErrors.budget_reset_frequency?.[0];
        const expiresInError = fieldErrors.expiresIn?.[0];

        if (nameError) {
          setError("name", {
            type: "manual",
            message: nameError,
          });
        }

        if (projectError) {
          setError("projectId", {
            type: "manual",
            message: projectError,
          });
        }

        if (scopesError) {
          setError("scopes", {
            type: "manual",
            message: scopesError,
          });
          return;
        }

        if (maxBudgetError) {
          setError("max_budget_usd", {
            type: "manual",
            message: maxBudgetError,
          });
          return;
        }

        if (budgetResetFrequencyError) {
          setError("budget_reset_frequency", {
            type: "manual",
            message: budgetResetFrequencyError,
          });
          return;
        }

        if (expiresInError) {
          setError("expiresIn", {
            type: "manual",
            message: expiresInError,
          });
          return;
        }

        return;
      }

      if (parsedForm.success) {
        createKey(parsedForm.data);
      }
    },
    [
      clearErrors,
      createKey,
      getValues,
      isProjectsLoading,
      projectsQuery.isError,
      setError,
      t,
      validationSchema,
    ],
  );

  return (
    <>
      <Dialog
        open={open && !createdKey}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isCreatingKey) {
            handleFormClose();
          }
        }}
      >
        <DialogContent
          className="bg-card border-border sm:max-w-lg data-[state=open]:slide-in-from-bottom-4 data-[state=open]:duration-300"
          showCloseButton={!isCreatingKey}
        >
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl font-bold">
              {t("apiKeys.createNewKey")}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateKey} className="flex flex-col gap-5 pt-2">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="key-name"
                className="text-foreground text-sm font-semibold"
              >
                {t("apiKeys.keyNameLabel")}
              </label>
              <Input
                id="key-name"
                placeholder={t("apiKeys.keyNamePlaceholder")}
                className={`bg-input text-foreground placeholder:text-muted-foreground h-11 rounded-[8px] px-4 py-3 ${
                  errors.name
                    ? "border-destructive focus-visible:ring-destructive/20"
                    : "border-border"
                }`}
                aria-invalid={Boolean(errors.name)}
                {...register("name", {
                  onChange: (event) => {
                    if (event.target.value.trim()) {
                      clearErrors("name");
                    }
                  },
                })}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-foreground text-sm font-semibold">
                {t("apiKeys.selectProject")}
              </label>
              <Controller
                name="projectId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (value) {
                        clearErrors("projectId");
                      }
                    }}
                    disabled={isProjectsLoading || projects.length === 0}
                  >
                    <SelectTrigger
                      className={`bg-input text-foreground h-11 w-full rounded-[8px] px-4 py-3 ${
                        errors.projectId
                          ? "border-destructive focus-visible:ring-destructive/20"
                          : "border-border"
                      }`}
                      aria-invalid={Boolean(errors.projectId)}
                    >
                      <SelectValue
                        placeholder={
                          projectsQuery.isLoading
                            ? t("apiKeys.loadingProjects")
                            : t("apiKeys.selectProject")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {projects.map((project) => {
                        const label =
                          project.name ?? project.displayName ?? project.id;

                        return (
                          <SelectItem key={project.id} value={project.id}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              />
              {isProjectsLoading && (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Spinner className="size-3.5" />
                  <span>{t("apiKeys.loadingProjects")}</span>
                </div>
              )}
              {projectsQuery.isError && (
                <p className="text-sm text-red-500">
                  {t("apiKeys.failedToLoadProjects")}
                </p>
              )}
              {!projectsQuery.isLoading &&
                !projectsQuery.isError &&
                projects.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t("apiKeys.noProjectsAvailable")}
                  </p>
                )}
              {errors.projectId && (
                <p className="text-sm text-red-500">
                  {errors.projectId.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-foreground text-sm font-semibold">
                {t("apiKeys.scopes")}
              </label>
              <Controller
                name="scopes"
                control={control}
                render={({ field }) => (
                  <Popover open={scopesOpen} onOpenChange={setScopesOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={scopesOpen}
                        aria-invalid={Boolean(errors.scopes)}
                        className={cn(
                          "border bg-input text-foreground flex min-h-9 w-full items-center justify-between gap-2 rounded-[8px] px-3 py-2 text-start",
                          errors.scopes &&
                            "border-destructive focus-visible:ring-destructive/20",
                        )}
                      >
                        <span className="flex min-w-0 flex-wrap gap-1">
                          {field.value.length > 0 ? (
                            field.value.map((scope) => (
                              <Badge key={scope} variant="outline">
                                {t(`apiKeys.scopeOptions.${scope}`)}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {t("apiKeys.selectScopes")}
                            </span>
                          )}
                        </span>
                        <ChevronDown className="text-muted-foreground size-4 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="z-[1500] w-[var(--radix-popover-trigger-width)] p-1"
                    >
                      {API_KEY_SCOPES.map((scope) => {
                        const selected = field.value.includes(scope);
                        return (
                          <button
                            key={scope}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className="hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm"
                            onClick={() => {
                              const nextScopes = selected
                                ? field.value.filter((value) => value !== scope)
                                : [...field.value, scope];
                              field.onChange(nextScopes);
                              clearErrors("scopes");
                            }}
                          >
                            <span
                              className={cn(
                                "border-primary flex size-4 items-center justify-center rounded-sm border",
                                selected
                                  ? "bg-primary text-primary-foreground"
                                  : "opacity-50",
                              )}
                            >
                              {selected && <Check className="size-3" />}
                            </span>
                            {t(`apiKeys.scopeOptions.${scope}`)}
                          </button>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                )}
              />
              {errors.scopes && (
                <p className="text-sm text-red-500">{errors.scopes.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="key-max-budget"
                className="text-foreground text-sm font-semibold"
              >
                {t("apiKeys.maxBudget")}
              </label>
              <Input
                id="max_budget_usd"
                placeholder={t("apiKeys.maxBudget")}
                type="number"
                min="0"
                step="0.01"
                className={`bg-input text-foreground placeholder:text-muted-foreground h-11 rounded-[8px] px-4 py-3 ${
                  errors.max_budget_usd
                    ? "border-destructive focus-visible:ring-destructive/20"
                    : "border-border"
                }`}
                aria-invalid={Boolean(errors.max_budget_usd)}
                {...register("max_budget_usd", {
                  onChange: (event) => {
                    if (event.target.value.trim()) {
                      clearErrors("max_budget_usd");
                    }
                  },
                })}
              />
              {errors.max_budget_usd && (
                <p className="text-sm text-red-500">
                  {errors.max_budget_usd.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="key-expiry"
                className="text-foreground text-sm font-semibold"
              >
                {t("apiKeys.budgetResetFrequency")}
              </label>
              <Controller
                name="budget_reset_frequency"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      const actualValue = value === "NONE" ? "" : value;

                      field.onChange(actualValue);

                      clearErrors("budget_reset_frequency");
                    }}
                  >
                    <SelectTrigger
                      className={`border-border bg-input text-foreground h-11 w-full rounded-[8px] px-4 py-3 ${
                        errors.budget_reset_frequency
                          ? "border-destructive focus-visible:ring-destructive/20"
                          : ""
                      }`}
                    >
                      <SelectValue
                        placeholder={t("apiKeys.budgetResetFrequency")}
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="NONE">{t("common.none")}</SelectItem>
                      <SelectItem value="HOURLY">
                        {t("apiKeys.hourly")}
                      </SelectItem>
                      <SelectItem value="DAILY">
                        {t("apiKeys.daily")}
                      </SelectItem>
                      <SelectItem value="WEEKLY">
                        {t("apiKeys.weekly")}
                      </SelectItem>
                      <SelectItem value="MONTHLY">
                        {t("apiKeys.monthly")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.budget_reset_frequency && (
                <p className="text-sm text-red-500">
                  {errors.budget_reset_frequency.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="key-expiry"
                className="text-foreground text-sm font-semibold"
              >
                {t("apiKeys.expiresIn")}
              </label>
              <Controller
                name="expiresIn"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="border-border bg-input text-foreground h-11 w-full rounded-[8px] px-4 py-3">
                      <SelectValue
                        placeholder={t("apiKeys.selectExpiration")}
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="7">{t("apiKeys.7days")}</SelectItem>
                      <SelectItem value="30">{t("apiKeys.30days")}</SelectItem>
                      <SelectItem value="60">{t("apiKeys.60days")}</SelectItem>
                      <SelectItem value="90">{t("apiKeys.90days")}</SelectItem>
                      <SelectItem value="180">
                        {t("apiKeys.6months")}
                      </SelectItem>
                      <SelectItem value="365">{t("apiKeys.1year")}</SelectItem>
                      <SelectItem value="never">{t("common.never")}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.expiresIn && (
                <p className="text-sm text-red-500">
                  {errors.expiresIn.message}
                </p>
              )}
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleFormClose}
                className="border-border bg-card text-foreground hover:bg-accent px-6"
                disabled={isCreatingKey}
              >
                {t("common.discard")}
              </Button>
              <Button
                type="submit"
                disabled={isCreatingKey}
                className="bg-primary text-primary-foreground hover:bg-primary/90 px-6"
                aria-busy={isCreatingKey}
              >
                {isCreatingKey ? (
                  <>
                    <Spinner className="size-4" />
                    <span>{t("common.creating")}</span>
                  </>
                ) : (
                  t("apiKeys.createKey")
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ApiKeySuccessDialog
        open={createdKey !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleCreatedKeyClose();
          }
        }}
        title={t("apiKeys.keyCreated")}
        apiKey={createdKey}
        note={t("apiKeys.saveKeyNote")}
      />
    </>
  );
};

export default CreateApiKeyDialog;
